import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  MessageSquare, Send, RefreshCw, CheckCircle2, XCircle, Clock, Activity, AlertTriangle, FileText,
  Stethoscope, Wifi, Copy, ShieldAlert,
} from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormField } from "@/components/common/FormSection";
import { api, formatApiError } from "@/lib/api";
import SmsTemplatesPanel from "@/pages/SmsTemplatesPanel";

const STATUS_BADGE = {
  sent:    { bg: "#D1FAE5", text: "#065F46", icon: CheckCircle2 },
  mocked:  { bg: "#E0E7FF", text: "#3730A3", icon: Clock },
  failed:  { bg: "#FEE2E2", text: "#991B1B", icon: XCircle },
  skipped: { bg: "#FEF3C7", text: "#92400E", icon: AlertTriangle },
  queued:  { bg: "#F1F5F9", text: "#475569", icon: Clock },
};

const KPI_COLORS = {
  sent: "#10B981", failed: "#EF4444", mocked: "#6366F1",
  skipped: "#F59E0B", queued: "#64748B",
};

function StatusPill({ status }) {
  const cfg = STATUS_BADGE[status] || STATUS_BADGE.queued;
  const Icon = cfg.icon;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider"
      style={{ background: cfg.bg, color: cfg.text }}
      data-testid={`sms-status-${status}`}
    >
      <Icon className="w-3 h-3" />
      {status}
    </span>
  );
}

function Kpi({ label, value, accent, icon: Icon }) {
  return (
    <div className="hah-card p-3 flex items-start gap-3">
      <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${accent}14`, color: accent }}>
        <Icon className="w-4 h-4" />
      </span>
      <div>
        <div className="text-2xl font-bold text-[#1F2937]">{value ?? 0}</div>
        <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280]">{label}</div>
      </div>
    </div>
  );
}

export default function SmsConsole() {
  const [status, setStatus] = useState(null);
  const [stats, setStats] = useState({ total: 0, by_status: {}, by_event_type: [] });
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState({ event_type: "", status: "", q: "" });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openTest, setOpenTest] = useState(false);
  const [openLog, setOpenLog] = useState(null);
  const [licenseScanRunning, setLicenseScanRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, st, l] = await Promise.all([
        api.get("/sms/status"),
        api.get("/sms/stats"),
        api.get("/sms/messages", {
          params: {
            page,
            page_size: 50,
            event_type: filter.event_type || undefined,
            status: filter.status || undefined,
            q: filter.q || undefined,
          },
        }),
      ]);
      setStatus(s.data);
      setStats(st.data);
      setItems(l.data.items || []);
      setTotal(l.data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [page, filter]);

  useEffect(() => { load(); }, [load]);

  async function runLicenseScan() {
    setLicenseScanRunning(true);
    try {
      const r = await api.post("/sms/license-scan", null, { params: { days_window: 30 } });
      toast.success(`License scan complete — near-expiry ${r.data.near_expiry_sent}, expired ${r.data.expired_sent}, skipped ${r.data.skipped}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLicenseScanRunning(false);
    }
  }

  const pages = Math.max(1, Math.ceil(total / 50));

  return (
    <div data-testid="sms-console">
      <PageHeader
        title="SMS Console"
        subtitle="Outbound transactional messaging — powered by Unifonic."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={load} className="border-[#E5E7EB]" data-testid="sms-refresh">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
            </Button>
            <Button variant="outline" onClick={runLicenseScan} disabled={licenseScanRunning} className="border-[#E5E7EB]" data-testid="sms-license-scan">
              <Activity className="w-4 h-4 mr-1.5" /> Run License Scan
            </Button>
            <Dialog open={openTest} onOpenChange={setOpenTest}>
              <DialogTrigger asChild>
                <Button className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="sms-test-open">
                  <Send className="w-4 h-4 mr-1.5" /> Send Test SMS
                </Button>
              </DialogTrigger>
              <TestSendDialog
                onClose={() => { setOpenTest(false); load(); }}
                templates={status?.templates || []}
              />
            </Dialog>
          </div>
        }
      />

      <Tabs defaultValue="messages">
        <TabsList className="mb-3">
          <TabsTrigger value="messages" data-testid="sms-tab-messages">
            <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> Messages
          </TabsTrigger>
          <TabsTrigger value="templates" data-testid="sms-tab-templates">
            <FileText className="w-3.5 h-3.5 mr-1.5" /> Templates
          </TabsTrigger>
          <TabsTrigger value="diagnostics" data-testid="sms-tab-diagnostics">
            <Stethoscope className="w-3.5 h-3.5 mr-1.5" /> Diagnostics
          </TabsTrigger>
        </TabsList>

        <TabsContent value="messages">
      {/* Provider status banner */}
      {status && (
        <div
          className={`hah-card p-3 mb-4 flex items-center gap-3 ${status.enabled ? "border-l-4 border-l-[#10B981]" : "border-l-4 border-l-[#F59E0B]"}`}
          data-testid="sms-provider-banner"
        >
          <div className="flex-1 text-sm">
            <strong className="text-[#1F2937]">{status.enabled ? "Provider live" : "Provider in mocked mode"}</strong>
            <span className="text-[#6B7280] ml-2">
              · Unifonic · Sender ID <span className="font-mono font-semibold">{status.sender_id}</span>
              · {status.template_count} templates · {status.log_count} messages logged
            </span>
          </div>
          {!status.enabled && (
            <span className="text-[11px] text-[#92400E] bg-[#FEF3C7] px-2 py-1 rounded">
              Set UNIFONIC_APPSID in backend/.env to enable real delivery
            </span>
          )}
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <Kpi label="Total" value={stats.total} accent="#0B7E8C" icon={MessageSquare} />
        <Kpi label="Sent" value={stats.by_status?.sent} accent={KPI_COLORS.sent} icon={CheckCircle2} />
        <Kpi label="Failed" value={stats.by_status?.failed} accent={KPI_COLORS.failed} icon={XCircle} />
        <Kpi label="Mocked" value={stats.by_status?.mocked} accent={KPI_COLORS.mocked} icon={Clock} />
        <Kpi label="Skipped (no phone)" value={stats.by_status?.skipped} accent={KPI_COLORS.skipped} icon={AlertTriangle} />
        <Kpi label="Queued" value={stats.by_status?.queued} accent={KPI_COLORS.queued} icon={Clock} />
      </div>

      {/* Filters */}
      <div className="hah-card p-3 mb-3 flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[180px]">
          <label className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280]">Search</label>
          <Input
            placeholder="Phone, body…"
            value={filter.q}
            onChange={(e) => { setFilter({ ...filter, q: e.target.value }); setPage(1); }}
            data-testid="sms-filter-q"
          />
        </div>
        <div className="w-48">
          <label className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280]">Event Type</label>
          <Select value={filter.event_type || "all"} onValueChange={(v) => { setFilter({ ...filter, event_type: v === "all" ? "" : v }); setPage(1); }}>
            <SelectTrigger data-testid="sms-filter-event"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All event types</SelectItem>
              {(status?.templates || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-40">
          <label className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280]">Status</label>
          <Select value={filter.status || "all"} onValueChange={(v) => { setFilter({ ...filter, status: v === "all" ? "" : v }); setPage(1); }}>
            <SelectTrigger data-testid="sms-filter-status"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="mocked">Mocked</SelectItem>
              <SelectItem value="skipped">Skipped</SelectItem>
              <SelectItem value="queued">Queued</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Log table */}
      <div className="hah-card overflow-hidden">
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className="bg-[#F5F7FA] text-[10px] uppercase tracking-wider text-[#6B7280]">
            <tr>
              <th className="text-left p-2.5">Status</th>
              <th className="text-left p-2.5">Event</th>
              <th className="text-left p-2.5">Recipient</th>
              <th className="text-left p-2.5">Body</th>
              <th className="text-left p-2.5">Created</th>
              <th className="text-left p-2.5">Provider ID / Error</th>
            </tr>
          </thead>
          <tbody>
            {items.map((m) => (
              <tr
                key={m.id}
                className="border-t border-[#E5E7EB] hover:bg-[#FAFBFC] cursor-pointer"
                onClick={() => setOpenLog(m)}
                data-testid={`sms-row-${m.id}`}
              >
                <td className="p-2.5"><StatusPill status={m.status} /></td>
                <td className="p-2.5 font-mono text-[11px] text-[#1F2937]">{m.event_type}</td>
                <td className="p-2.5 font-mono text-[11px] text-[#1F2937]">{m.recipient || m.recipient_raw || "—"}</td>
                <td className="p-2.5 text-[#475569] max-w-md truncate">{m.body || "—"}</td>
                <td className="p-2.5 text-[#6B7280] text-xs">{m.created_at ? new Date(m.created_at).toLocaleString() : "—"}</td>
                <td className="p-2.5 text-[11px]">
                  {m.provider_message_id && <span className="font-mono text-[#0B7E8C]">{m.provider_message_id}</span>}
                  {m.error && <span className="text-[#991B1B]">{m.error}</span>}
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="p-10 text-center text-sm text-[#6B7280]">No SMS messages logged yet.</td></tr>
            )}
          </tbody>
        </table></div>
        <div className="flex items-center justify-between p-3 border-t border-[#E5E7EB] text-xs text-[#6B7280]">
          <span data-testid="sms-pagination-info">Page {page} of {pages} · {total} total</span>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} className="border-[#E5E7EB] h-7 text-xs" data-testid="sms-prev">Prev</Button>
            <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)} className="border-[#E5E7EB] h-7 text-xs" data-testid="sms-next">Next</Button>
          </div>
        </div>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!openLog} onOpenChange={(o) => !o && setOpenLog(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>SMS Message Detail</DialogTitle></DialogHeader>
          {openLog && (
            <div className="space-y-3 text-sm" data-testid="sms-log-detail">
              <div className="flex items-center gap-2"><StatusPill status={openLog.status} /> <span className="font-mono text-xs text-[#6B7280]">{openLog.id}</span></div>
              <div className="grid grid-cols-2 gap-2">
                <Info label="Event Type" value={openLog.event_type} mono />
                <Info label="Recipient" value={openLog.recipient || openLog.recipient_raw} mono />
                <Info label="Created" value={openLog.created_at && new Date(openLog.created_at).toLocaleString()} />
                <Info label="Provider Message ID" value={openLog.provider_message_id} mono />
              </div>
              <Info label="Body" value={openLog.body} block />
              {openLog.error && <Info label="Error" value={openLog.error} block error />}
              {openLog.provider_response && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280] mb-1">Provider Response</div>
                  <pre className="bg-[#F5F7FA] p-2 rounded text-[11px] overflow-x-auto font-mono">
                    {JSON.stringify(openLog.provider_response, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
        </TabsContent>

        <TabsContent value="templates">
          <SmsTemplatesPanel />
        </TabsContent>

        <TabsContent value="diagnostics">
          <DiagnosticsPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Info({ label, value, mono, block, error }) {
  return (
    <div className={block ? "" : ""}>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280]">{label}</div>
      <div className={`text-sm ${mono ? "font-mono text-[12px]" : ""} ${error ? "text-[#991B1B]" : "text-[#1F2937]"}`}>{value || "—"}</div>
    </div>
  );
}

function TestSendDialog({ onClose, templates }) {
  const [eventType, setEventType] = useState(templates[0] || "patient_welcome");
  const [recipient, setRecipient] = useState("");
  const [varsText, setVarsText] = useState('{"patient_name": "Test Patient", "mrn": "TEST-0001"}');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // Reset event_type if templates list arrives later
  useEffect(() => {
    if (templates.length && !templates.includes(eventType)) setEventType(templates[0]);
  }, [templates, eventType]);

  const exampleVars = useMemo(() => ({
    rpm_critical_alert: { patient_name: "Khulood Al-Maliki", mrn: "HAH-2026-0001", vital_type: "spo2", value: "85%", severity: "CRITICAL" },
    hotline_escalation: { record_id: "HL-2026-0042", patient_name: "Hassan Al-Awfi", reason: "Severe chest pain" },
    teleconsult_reminder: { when: "Tomorrow 09:30 KSA" },
    onboarding_status_update: { status: "Approved for home care", detail: "Visit being scheduled." },
    fleet_visit_notification: { eta: "~20 minutes" },
    patient_welcome: { patient_name: "Test Patient", mrn: "TEST-0001" },
    user_activated: { email: "user@hah.health" },
    user_deactivated: { email: "user@hah.health" },
    license_near_expiry: { name: "Dr. Reem Al-Omari", license_number: "MD-12345", days_left: 14, expiry_date: "2026-07-01" },
    license_expired: { name: "Dr. Reem Al-Omari", license_number: "MD-12345", expiry_date: "2026-05-01" },
    license_renewed: { name: "Dr. Reem Al-Omari", license_number: "MD-12345", expiry_date: "2027-05-01" },
  }), []);

  useEffect(() => {
    const ex = exampleVars[eventType];
    if (ex) setVarsText(JSON.stringify(ex, null, 2));
  }, [eventType, exampleVars]);

  async function send() {
    if (!recipient) { toast.error("Recipient phone required"); return; }
    let variables = {};
    try { variables = JSON.parse(varsText || "{}"); }
    catch { toast.error("Variables must be valid JSON"); return; }
    setSending(true);
    setResult(null);
    try {
      const r = await api.post("/sms/test-send", { event_type: eventType, recipient, variables });
      setResult(r.data);
      if (r.data.status === "sent") toast.success("Sent successfully");
      else if (r.data.status === "mocked") toast.success("Mocked (provider disabled) — message logged");
      else toast.error(`Status: ${r.data.status} — ${r.data.error || "see response"}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  }

  return (
    <DialogContent className="max-w-lg">
      <DialogHeader><DialogTitle>Send Test SMS</DialogTitle></DialogHeader>
      <div className="space-y-3 py-2" data-testid="sms-test-dialog">
        <FormField label="Event Type">
          <Select value={eventType} onValueChange={setEventType}>
            <SelectTrigger data-testid="sms-test-event"><SelectValue /></SelectTrigger>
            <SelectContent>
              {templates.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Recipient (international format)">
          <Input
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            placeholder="+966 5XXXXXXXX"
            data-testid="sms-test-recipient"
          />
        </FormField>
        <FormField label="Variables (JSON)">
          <Textarea rows={6} value={varsText} onChange={(e) => setVarsText(e.target.value)} className="font-mono text-xs" data-testid="sms-test-vars" />
        </FormField>
        {result && (
          <div className="border-l-4 p-2 text-xs rounded bg-[#F5F7FA]" style={{ borderColor: result.status === "sent" ? "#10B981" : result.status === "mocked" ? "#6366F1" : "#EF4444" }}>
            <div className="flex items-center gap-2 mb-1"><StatusPill status={result.status} /></div>
            <div className="text-[#475569]"><strong>Body:</strong> {result.body}</div>
            {result.error && <div className="text-[#991B1B] mt-1">{result.error}</div>}
          </div>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} className="border-[#E5E7EB]">Close</Button>
        <Button onClick={send} disabled={sending} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="sms-test-send-btn">
          {sending ? "Sending…" : "Send"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function DiagnosticsPanel() {
  const [phone, setPhone] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  async function run(withSend) {
    if (withSend && !phone.trim()) { toast.error("Enter a phone number for a live test"); return; }
    setRunning(true);
    try {
      const r = await api.get("/sms/diagnostics", {
        params: withSend ? { to: phone.trim() } : {},
      });
      setResult(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning(false);
    }
  }

  function copyIp(ip) {
    if (!ip) return;
    navigator.clipboard?.writeText(ip).then(
      () => toast.success(`Copied ${ip}`),
      () => toast.error("Copy failed — select manually"),
    );
  }

  const cfg = result?.config;
  const egress = result?.egress_ip;
  const lt = result?.live_test;
  const liveFailed = lt && !lt.skipped && lt.status !== "sent";
  const ipBlock = liveFailed && (lt.error === "ER-01" || /invalid ip/i.test(lt.error || ""));
  const dupBlock = liveFailed && (lt.error === "ER-58" || /duplicated/i.test(lt.error_message || ""));

  return (
    <div data-testid="sms-diagnostics-panel" className="space-y-4">
      <div className="hah-card p-4">
        <div className="flex items-start gap-3 mb-3">
          <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-[#0B7E8C14] text-[#0B7E8C]">
            <Stethoscope className="w-4 h-4" />
          </span>
          <div>
            <div className="font-semibold text-[#1F2937]">Unifonic Production Diagnostic</div>
            <div className="text-xs text-[#6B7280] mt-0.5 max-w-2xl">
              Run this on each environment to see its live outbound IP and the exact provider error.
              If a send fails with <span className="font-mono">ER-01 / Invalid IP</span>, whitelist the
              egress IP shown below in your Unifonic console (Developers → Applications → Security).
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[220px]">
            <label className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280]">Test phone (optional)</label>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+966 5XXXXXXXX"
              data-testid="sms-diag-phone"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => run(false)}
            disabled={running}
            className="border-[#E5E7EB]"
            data-testid="sms-diag-check-btn"
          >
            <Wifi className="w-4 h-4 mr-1.5" /> Check config &amp; IP
          </Button>
          <Button
            onClick={() => run(true)}
            disabled={running}
            className="bg-[#0B7E8C] hover:bg-[#128C97] text-white"
            data-testid="sms-diag-run-btn"
          >
            <Send className="w-4 h-4 mr-1.5" /> {running ? "Running…" : "Run live test"}
          </Button>
        </div>
      </div>

      {result && (
        <>
          {/* Egress IP — the key actionable value */}
          <div className="hah-card p-4 border-l-4 border-l-[#0B7E8C]" data-testid="sms-diag-egress">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280] mb-1">Outbound (egress) IP — whitelist this in Unifonic</div>
            {egress?.ip ? (
              <div className="flex items-center gap-2">
                <span className="font-mono text-xl font-bold text-[#1F2937]" data-testid="sms-diag-ip">{egress.ip}</span>
                <Button size="sm" variant="outline" className="border-[#E5E7EB] h-7" onClick={() => copyIp(egress.ip)} data-testid="sms-diag-copy-ip">
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                </Button>
              </div>
            ) : (
              <div className="text-sm text-[#991B1B]">Could not resolve egress IP ({egress?.error || "unknown"})</div>
            )}
          </div>

          {/* IP-block call-out */}
          {ipBlock && (
            <div className="hah-card p-4 border-l-4 border-l-[#EF4444] bg-[#FEF2F2]" data-testid="sms-diag-ipblock">
              <div className="flex items-center gap-2 text-[#991B1B] font-semibold text-sm mb-1">
                <ShieldAlert className="w-4 h-4" /> IP not whitelisted (ER-01)
              </div>
              <div className="text-xs text-[#7F1D1D]">
                Unifonic rejected this IP. Add <span className="font-mono font-bold">{egress?.ip}</span> to the
                AppSid's allowed IP list (or disable IP restriction), then run the live test again.
              </div>
            </div>
          )}

          {/* Duplicate-message call-out */}
          {dupBlock && (
            <div className="hah-card p-4 border-l-4 border-l-[#F59E0B] bg-[#FFFBEB]" data-testid="sms-diag-dupblock">
              <div className="flex items-center gap-2 text-[#92400E] font-semibold text-sm mb-1">
                <ShieldAlert className="w-4 h-4" /> Duplicated message (ER-58)
              </div>
              <div className="text-xs text-[#78350F]">
                Unifonic blocks sending the <span className="font-semibold">same text to the same number</span> within
                a short window (anti-spam). This is not a configuration problem — wait a few minutes, or test with a
                different number. Real patient SMS are unaffected because each message has unique content.
              </div>
            </div>
          )}

          {/* Config */}
          <div className="hah-card p-4">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280] mb-2">Configuration</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <DiagItem label="Service enabled" ok={cfg?.service_enabled} value={cfg?.service_enabled ? "Yes" : "No (mocked)"} />
              <DiagItem label="AppSid present" ok={cfg?.app_sid_present} value={cfg?.app_sid_preview || (cfg?.app_sid_present ? "Yes" : "Missing")} mono />
              <Info label="Sender ID" value={cfg?.sender_id} mono />
              <Info label="SMS_ENABLED flag" value={cfg?.sms_enabled_flag} mono />
              <Info label="Endpoint" value={cfg?.endpoint} mono />
              <Info label="Checked at" value={result.checked_at && new Date(result.checked_at).toLocaleString()} />
            </div>
          </div>

          {/* Live test result */}
          {lt && (
            <div className="hah-card p-4" data-testid="sms-diag-livetest">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280] mb-2">Live test send</div>
              {lt.skipped ? (
                <div className="text-sm text-[#6B7280]">{lt.reason}</div>
              ) : (
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <StatusPill status={lt.status} />
                    <span className="font-mono text-xs text-[#6B7280]">{lt.recipient_normalized || lt.recipient_raw}</span>
                  </div>
                  {lt.error && <Info label="Error" value={lt.error_message ? `${lt.error} — ${lt.error_message}` : lt.error} block error />}
                  {lt.provider_message_id && <Info label="Provider Message ID" value={lt.provider_message_id} mono />}
                  {lt.provider_response && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280] mb-1">Provider Response</div>
                      <pre className="bg-[#F5F7FA] p-2 rounded text-[11px] overflow-x-auto font-mono">
                        {JSON.stringify(lt.provider_response, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function DiagItem({ label, ok, value, mono }) {
  const Icon = ok ? CheckCircle2 : XCircle;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280]">{label}</div>
      <div className={`text-sm flex items-center gap-1 ${mono ? "font-mono text-[12px]" : ""} ${ok ? "text-[#065F46]" : "text-[#991B1B]"}`}>
        <Icon className="w-3.5 h-3.5" /> {value || "—"}
      </div>
    </div>
  );
}
